import { useState, useContext } from "react";
import { Polyline, useMapEvents } from "react-leaflet";
import { v4 as uuid } from "uuid";
import detectIt from "detect-it";
import { ToolContext } from "../context/ToolContext";
import {
  getLastPoint,
  createDrawCableHelpers,
  determinePointToAdd,
  createHintLinePositions,
} from "../utils/cableToolUtils";
import { isValidCoordinate, pushCoordinateIfNew } from "../utils/coordinateUtils";
import { LatLng } from "../model/viewModel/simpleTypes";

interface CableToolProps {
  overrideLength: number;
  create: (cable: { geometry: LatLng[] }, finalize?: boolean) => void;
}

const CableTool = ({ overrideLength, create }: CableToolProps) => {
  const { toolState, setToolState } = useContext(ToolContext);
  const { drawCable } = toolState;
  const [cableId, setCableId] = useState<string | undefined>();
  const [currentPosition, setCurrentPosition] = useState<LatLng | null>(null);

  // Create helpers for handling drawCable dual nature
  const drawCableHelpers = createDrawCableHelpers(drawCable, toolState, setToolState);

  // Add point to cable geometry
  const addPointToCable = (point: LatLng) => {
    if (!drawCableHelpers.isDrawing() || !isValidCoordinate(point)) return;

    const currentCable = drawCableHelpers.getCableObject();
    const newGeometry = pushCoordinateIfNew(currentCable.geometry, point);
    if (newGeometry !== currentCable.geometry) {
      const updatedCable = {
        ...currentCable,
        geometry: newGeometry,
      };

      drawCableHelpers.updateCable(updatedCable);
      setCableId(uuid());
    }
  };

  useMapEvents({
    mousemove(e) {
      if (isValidCoordinate(e.latlng)) {
        setCurrentPosition(e.latlng);
      }
    },

    click(e) {
      const clickPosition = e.latlng;
      if (!drawCableHelpers.isDrawing() || !isValidCoordinate(clickPosition)) return;

      const currentGeometry = drawCableHelpers.getGeometry();
      const pointToAdd = determinePointToAdd(
        clickPosition,
        overrideLength,
        currentGeometry,
        getLastPoint,
      );

      addPointToCable(pointToAdd);
    },

    dblclick(e) {
      if (!drawCableHelpers.isDrawing() || !isValidCoordinate(e.latlng)) return;
      const currentCable = drawCableHelpers.getCableObject();
      if (currentCable.geometry.length < 2) return;
      const endpoint = getLastPoint(currentCable.geometry);
      if (endpoint?.lat == null || endpoint?.lng == null) return;
      create({ ...currentCable }, true);
    },
  });

  if (!drawCableHelpers.isDrawing()) return null;

  const geometry = drawCableHelpers.getGeometry();
  const lastPoint = getLastPoint(geometry);
  const hintLinePositions = createHintLinePositions(lastPoint, currentPosition);

  return (
    <>
      {geometry.length > 1 && <Polyline key={`cable-${cableId}`} positions={geometry} />}
      {detectIt.hasMouse && hintLinePositions.length === 2 && (
        <Polyline
          key={`hint-${cableId}`}
          positions={hintLinePositions}
          pathOptions={{
            dashArray: "5 10",
            opacity: 0.6,
            weight: 2,
          }}
        />
      )}
    </>
  );
};

export default CableTool;
